import { useConfirm } from "material-ui-confirm";
import { useState } from "react";
import { useParams } from "react-router";
import { useAppContext } from "../../../context/AuthContext";
import { ArticleController, ArticleControllerType } from "../../../controller/article/ArticleController";
import LabelsContext from "../../../lang/LabelsContext";
import { getTypeFromNameFile, handleErrorMessage, renameFile, uniqueBy, useEffectOnce } from "../../../utils/Utils";
import EnumDesignation from "../../../utils/enum/EnumDesignation";

export const FILTER_REVISION_VALUES = [
  {
    id: -2,
    name: "All",
  },
  {
    id: -1,
    name: "Active",
  },
];

export const FILTER_ALL = "all";
export const FILTER_LAST = "last";

const EXCLUDE_TYPE = [EnumDesignation.OVERVIEW];

const EXCLUDE_FILES = [
  EnumDesignation.TABLES,
  EnumDesignation.FIGURE_PDF,
  EnumDesignation.FIGURE,
  EnumDesignation.SUPPLEMENTARY_FIGURE,
  EnumDesignation.POWERPOINTER_PRESENTATION,
  EnumDesignation.ADDITIONAL_FILE,
  EnumDesignation.VIDEO_FILE,
];

interface ManageFilesServiceType {
  article: any;
  openFileDialog: any;
  openFileDialogAction: any;
  closeFileDialogAction: any;
  designationOptions: any;
  uploadFile: any;
  handleResetDialog: any;
  files: any;
  uploading: boolean;
  filesByAssistant: any;
  deleteFile: (fileId: any) => void;
  revisionSelectedOptionAssistent: any;
  setRevisionSelectedOptionAssistent: any;
  updateAssitentFiles: (filesNew: any, value: string) => any;
  selectedDesignation: any;
  openDialogRegenratePdf: () => void;
  downloadAllFigures: () => void;
}

export default function ManageFilesService(): ManageFilesServiceType {
  const { Labels } = LabelsContext();
  const {
    axiosGetArticleById,
    axiosUploadFile,
    axiosPostDeleteFile,
    axiosPostArticleRegenerateCombinedPdf,
    axiosPostAddFile,
  }: ArticleControllerType = ArticleController();
  const { showMessage } = useAppContext();
  const confirm = useConfirm();

  const [article, setArticle] = useState<any>([]);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [filesByAssistant, setFilesByAssistant] = useState<any>();
  const [filesList, setFilesList] = useState<any>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [selectedDesignation, setSelectedDesignation] = useState<any>();
  const [selectedFile, setSelectedFile] = useState<any>();

  const [revisionSelectedOptionAssistent, setRevisionSelectedOptionAssistent] = useState<any>();

  const { id }: any = useParams();

  const designationOptions: any = [
    { name: "Manuscript", code: EnumDesignation.MANUSCRIPT },
    { name: "Figure", code: EnumDesignation.FIGURE },
    { name: "Supplementary figure", code: EnumDesignation.SUPPLEMENTARY_FIGURE },
    { name: Labels.ADDITIONAL_FILE, code: EnumDesignation.ADDITIONAL_FILE },
    { name: Labels.VIDEO_FILE, code: EnumDesignation.VIDEO_FILE },
    { name: "Full manuscript", code: EnumDesignation.FULL_MANUSCRIPT },
    { name: "Letter of Rebuttal", code: EnumDesignation.LETTER_OF_REBUTAL },
    { name: "Letter of Rebuttal PDF", code: EnumDesignation.LETTER_OF_REBUTAL_PDF },
    { name: "FigurePDF", code: EnumDesignation.FIGURE_PDF },
    { name: "Unmarked Manuscript", code: EnumDesignation.UNMARKED_MANUSCRIPT },
    { name: "Marked manuscripts Manuscript", code: EnumDesignation.MARKED_MANUSCRIPTS },
    { name: "Marked manuscript PDF", code: EnumDesignation.MARKED_MANUSCRIPTS_PDF },
    { name: "Overview", code: EnumDesignation.OVERVIEW },
    { name: "Tables", code: EnumDesignation.TABLES },
    { name: "Cover letter", code: EnumDesignation.COVER_LETTER },
    { name: "Powerpointer presentation", code: EnumDesignation.POWERPOINTER_PRESENTATION },
    { name: "Abstract", code: EnumDesignation.ABSTRACT },
    { name: "Full Article", code: EnumDesignation.FULL_ARTICLE },
  ];

  const groupedDataFiles = (data: any) =>
    data?.reduce((acc: any, obj: any) => {
      if (!acc[obj.name] || new Date(obj.uploadDate) > new Date(acc[obj.name].uploadDate)) {
        acc[obj.name] = obj;
      }
      return acc;
    }, {});

  const updateAssitentFiles = (filesNew: any, value: string) => {
    if (value === FILTER_LAST) {
      const filesFilterNew: any = [];
      const uniqueByType = uniqueBy(filesNew, "designation");
      uniqueByType.forEach((uniqType: any) => {
        if (EXCLUDE_FILES.includes(uniqType)) {
          filesFilterNew.push(...filesNew?.filter((f: any) => f.designation === uniqType));
        } else {
          const maxIdByType = Math.max(
            ...filesNew.filter((f: any) => f.designation === uniqType).map((o: any) => o.id)
          );
          const filedFindBy = filesNew?.find((f: any) => f.designation === uniqType && f.id === maxIdByType);
          filesFilterNew.push(filedFindBy);
        }
      });
      return filesFilterNew;
    }

    return filesNew;
  };

  const setArticleState = (newArticle: any, newFileName?: number) => {
    setArticle(newArticle);
    const newFilesByAssistant: any = {};
    setFilesList(newArticle.files);
    const arrayNew = newArticle.files
      .filter((x: any) => x.revision === newArticle.revision && !EXCLUDE_TYPE.includes(x.type))
      .map((f: any, i: any) => ({
        id: f.id,
        name: f.filename,
        designation: f.type,
        revision: f.revision,
        type: getTypeFromNameFile(f.filename),
        location: f.location,
        uploadDate: f.uploadDate,
        new: f.filename === newFileName,
      }));
    const filesSortFindDistinct = Object.values(groupedDataFiles(arrayNew));
    newFilesByAssistant[newArticle.revision] = filesSortFindDistinct;
    setRevisionSelectedOptionAssistent(FILTER_LAST);
    setFilesByAssistant(newFilesByAssistant);
  };

  const loadFiles = () => {
    axiosGetArticleById(id)
      .then((result: any) => {
        setArticleState(result.data);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      });
  };

  useEffectOnce(loadFiles);

  const openFileDialogAction = (id: number) => {
    if (id) {
      setSelectedDesignation(filesByAssistant[article.revision].find((d: any) => d.id === id));
    }
    setSelectedFile(!isNaN(id) ? id : null);

    setOpenFileDialog(true);
  };

  const closeFileDialogAction = () => setOpenFileDialog(false);

  const deleteFile = (fileId: any) => {
    if (+fileId) {
      axiosPostDeleteFile(article.id, fileId)
        .then((response: any) => {
          loadFiles();
        })
        .catch((error: any) => handleErrorMessage(showMessage, error));
    } else {
      removeFileFromList(fileId);
    }
  };

  const removeFileFromList = (fileId: number) => {
    const lastRevision = Object.keys(filesByAssistant);
    const fileByLastRevision = filesByAssistant[lastRevision[0]].filter((file: any) => file.id !== fileId);
    setFilesByAssistant({
      ...filesByAssistant,
      [lastRevision[0]]: fileByLastRevision,
    });
  };

  const uploadFile = async (file: any, values: any) => {
    const type = getTypeFromNameFile(file[0].name);

    const newFile = renameFile(
      file[0],
      `${article.articleId}${values.designation.code}${filesList.length + 1}.${type}`
    );

    setUploading(true);
    axiosUploadFile(article.id, [newFile])
      .then(async (response: any) => {
        if (selectedFile) {
          await axiosPostDeleteFile(article.id, selectedFile);
        }

        axiosPostAddFile(article.id, {
          filename: response.data.fileName,
          type: values.designation.code,
          fileType: getTypeFromNameFile(newFile.name),
          revision: article.revision,
        })
          .then(({ data }: any) => {
            setArticleState(data, response.data.fileName);
            closeFileDialogAction();
            setUploading(false);
          })
          .catch((error: any) => handleErrorMessage(showMessage, error));
      })
      .catch((error: any) => {
        setUploading(false);
        handleErrorMessage(showMessage, error);
      });
  };

  const handleResetDialog = (disabled: boolean, setFieldValue: any, files: File[], size: any, type: any) => {
    if (files !== null) setFiles(files);
    setFieldValue("size", size);
    setFieldValue("type", type);
  };

  const openDialogRegenratePdf = () =>
    confirm({
      title: "Regenerate full article pdf",
      description: "New combined PDF file will be generated soon and will replace the old one. It may take a minute.",
      confirmationText: "OK",
      cancellationText: "Cancel",
      confirmationButtonProps: {
        variant: "contained",
        color: "primary",
      },
    })
      .then(() => axiosPostArticleRegenerateCombinedPdf(article.id))
      .catch(() => {});

  const downloadAllFigures = async () => {
    const figuresFiles = filesByAssistant[article.revision].filter(({ designation }: any) =>
      [EnumDesignation.FIGURE, EnumDesignation.SUPPLEMENTARY_FIGURE].includes(designation)
    );
    figuresFiles?.forEach(async (file: any) => {
      const image = await fetch(file.location);
      const imageBlog = await image.blob();
      const imageURL = URL.createObjectURL(imageBlog);

      const link = document.createElement("a");
      link.href = imageURL;
      link.download =
        `${file?.designation === EnumDesignation.SUPPLEMENTARY_FIGURE ? "supplementary-" : ""}${file.name}` ??
        file.location;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  return {
    article,
    openFileDialog,
    openFileDialogAction,
    closeFileDialogAction,
    designationOptions,
    uploadFile,
    handleResetDialog,
    files,
    uploading,
    filesByAssistant,
    deleteFile,
    revisionSelectedOptionAssistent,
    setRevisionSelectedOptionAssistent,
    updateAssitentFiles,
    selectedDesignation,
    openDialogRegenratePdf,
    downloadAllFigures,
  };
}

export type { ManageFilesServiceType };
